import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import QRCode from "react-qr-code";
import { register as apiRegister, login as apiLogin, fetchMe, forgotPassword } from "@/lib/api";
import { IceCreamCone, Coffee, LogOut, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const StampCard = ({ user, onLogout }) => {
  const stamps = user.stamps || 0;
  const full = stamps >= 9;
  return (
    <Reveal className="max-w-lg mx-auto">
      <div data-testid="loyalty-card" className="bg-teal text-cream rounded-[2rem] p-8 shadow-2xl shadow-teal/30 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-blush/20" aria-hidden="true" />
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Morelli's Loyalty</p>
        <h2 className="font-display font-bold text-3xl mt-1">{user.name}</h2>
        <p className="font-display italic text-lg text-cream/80 mt-4">
          {full ? "Your card is full — free treat time!" : stamps === 0 ? "Buy a coffee to start collecting" : `${9 - stamps} stamp${9 - stamps > 1 ? "s" : ""} to your free treat`}
        </p>
        <div className="grid grid-cols-9 gap-1.5 mt-5" data-testid="stamp-grid">
          {Array.from({ length: 9 }).map((_, i) => (
            <motion.span key={i} initial={false} animate={i < stamps ? { scale: [1, 1.3, 1] } : {}}
              data-testid={`stamp-${i}`}
              className={`aspect-square rounded-full grid place-items-center border ${i < stamps ? "bg-gold text-teal-deep border-gold" : "border-cream/30 text-cream/30"}`}>
              <IceCreamCone size={14} strokeWidth={2} />
            </motion.span>
          ))}
        </div>
        <div className="mt-7 bg-cream rounded-2xl p-4 flex flex-col items-center">
          <div className="bg-white p-4 rounded-xl" data-testid="member-qr">
            <QRCode value={`${window.location.origin}/staff?code=${user.id}`} size={180} fgColor="#5B2A86" />
          </div>
          <p className="text-teal/60 text-[11px] mt-3 tracking-wide">Staff scan this at the counter</p>
          <p className="text-teal font-semibold text-sm tracking-[0.3em] mt-1" data-testid="member-short-code">{user.id.slice(0, 8).toUpperCase()}</p>
        </div>
        <p className="text-center text-[11px] text-cream/50 mt-4">9 stamps = a free coffee or scoop &middot; Treats earned: {user.freebies || 0}</p>
      </div>
      <button onClick={onLogout} data-testid="loyalty-logout"
        className="mt-6 mx-auto flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-teal/60 hover:text-gold transition-colors">
        <LogOut size={14} /> Sign out
      </button>
    </Reveal>
  );
};

const Loyalty = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [bday, setBday] = useState({ day: "", month: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("morellis_token");
    if (!token) { setLoading(false); return; }
    fetchMe(token)
      .then((u) => {
        setUser(u);
        if (u.birthday_bonus) toast.success("Happy birthday! A bonus stamp is on us.");
      })
      .catch(() => localStorage.removeItem("morellis_token"))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await forgotPassword({ email: form.email });
        toast.success(res.message);
        setMode("login");
        return;
      }
      const fn = mode === "register" ? apiRegister : apiLogin;
      const payload = mode === "register"
        ? { ...form, ...(bday.day && bday.month ? { birthday_day: Number(bday.day), birthday_month: Number(bday.month) } : {}) }
        : { email: form.email, password: form.password };
      const data = await fn(payload);
      localStorage.setItem("morellis_token", data.token);
      toast.success(mode === "register" ? "Welcome to the Morelli's circle!" : "Welcome back!");
      const me = await fetchMe(data.token);
      if (me.birthday_bonus) toast.success("Happy birthday! A bonus stamp is on us.");
      setUser(me);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("morellis_token");
    setUser(null);
    setForm({ name: "", email: "", password: "" });
  };

  const LOYALTY_OPEN = false;

  if (!LOYALTY_OPEN) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-24 flex items-center justify-center" data-testid="loyalty-wip">
        <Reveal className="text-center px-6 max-w-lg mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-teal/60 font-medium">The Morelli&rsquo;s Circle</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-4 text-teal">Work in progress</h1>
          <div className="mt-6 h-px w-20 bg-gold mx-auto" />
          <p className="mt-6 text-teal/70 leading-relaxed">
            Our loyalty card is getting a little polish behind the counter and will be
            back shortly. Thank you for bearing with us — the next stamp will be worth the wait.
          </p>
          <a href="/" data-testid="loyalty-wip-home-link"
            className="mt-8 inline-block rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm px-10 py-4 hover:bg-gold hover:text-teal-deep transition-colors duration-300">
            Back to the Parlour
          </a>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-32 pb-24" data-testid="loyalty-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.4em] text-teal/70 font-medium">The Morelli's Circle</p>
          <h1 className="font-display font-bold tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mt-4 text-teal">
            Nine Stamps, <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-gold via-[#d9b878] to-gold">One Free Treat</span>
          </h1>
          <div className="mt-6 h-px w-24 bg-gold mx-auto" />
          <p className="mt-6 max-w-md mx-auto text-teal/70 text-sm md:text-base leading-relaxed">
            Every coffee earns a stamp. Nine stamps earn a free coffee or a free scoop of ice cream — our thank-you, now paper-free.
          </p>
        </Reveal>

        {loading ? (
          <p className="text-center font-display italic text-teal/50 text-xl">One moment...</p>
        ) : user ? (
          <StampCard user={user} onLogout={logout} />
        ) : (
          <Reveal className="max-w-md mx-auto">
            <div className="bg-white rounded-[2rem] border border-gold/25 shadow-xl shadow-teal/10 p-8">
              <div className="grid grid-cols-2 gap-2 mb-8" role="tablist">
                {[["register", "Join"], ["login", "Sign In"]].map(([v, label]) => (
                  <button key={v} role="tab" aria-selected={mode === v} data-testid={`auth-tab-${v}`} onClick={() => setMode(v)}
                    className={`rounded-full py-2.5 text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-300 ${mode === v ? "bg-teal text-cream" : "border border-teal/30 text-teal/70 hover:border-gold"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <form onSubmit={submit} data-testid="loyalty-auth-form" className="flex flex-col gap-4">
                {mode === "register" && (
                  <input required maxLength={60} placeholder="Your name" data-testid="auth-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors" />
                )}
                <input required type="email" placeholder="Email address" data-testid="auth-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors" />
                {mode !== "forgot" && (
                  <div className="relative">
                    <input required type={showPw ? "text" : "password"} minLength={6} placeholder="Password (6+ characters)" data-testid="auth-password" value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 pr-8 text-teal placeholder:text-teal/40 transition-colors" />
                    <button type="button" onClick={() => setShowPw(!showPw)} data-testid="toggle-password-visibility"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-teal/40 hover:text-gold transition-colors">
                      {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                )}
                {mode === "login" && (
                  <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")}
                    className="self-end text-[11px] uppercase tracking-[0.2em] text-teal/50 hover:text-gold transition-colors -mt-1">
                    Forgot password?
                  </button>
                )}
                {mode === "register" && (
                  <div data-testid="birthday-fields">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-teal/50 mt-1">Birthday — optional, earns a bonus stamp in your birthday month</p>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <select data-testid="birthday-day" value={bday.day} onChange={(e) => setBday({ ...bday, day: e.target.value })}
                        className="bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal transition-colors">
                        <option value="">Day</option>
                        {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                      </select>
                      <select data-testid="birthday-month" value={bday.month} onChange={(e) => setBday({ ...bday, month: e.target.value })}
                        className="bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal transition-colors">
                        <option value="">Month</option>
                        {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                          <option key={m} value={i + 1}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <button type="submit" disabled={busy} data-testid="auth-submit"
                  className="mt-4 w-full rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm py-4 hover:bg-gold hover:text-teal-deep transition-colors duration-300 active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy ? "One moment..." : mode === "register" ? <>Get My Card <IceCreamCone size={15} /></> : mode === "forgot" ? "Email Me a Reset Link" : <>Sign In <Coffee size={15} /></>}
                </button>
              </form>
              <p className="text-[11px] text-teal/50 text-center mt-5">Already stamping on paper? Bring your card in — we'll transfer your stamps.</p>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
};

export default Loyalty;
